import {
  routes, ApiError, type ApiErrorResponse,
  type ListBatchesRequest, type ListBatchesResponse,
  type GetBatchRequest, type GetBatchResponse,
  type CreateBatchRequest, type CreateBatchResponse,
  type RetryBatchRequest, type RetryBatchResponse,
  type CancelBatchRequest, type CancelBatchResponse,
  type BatchOverviewResponse,
  type ListAssetsRequest, type ListAssetsResponse,
  type RequestUploadUrlRequest, type RequestUploadUrlResponse,
  type ConfirmUploadRequest, type ConfirmUploadResponse,
  type AppConfig, type Ok,
  type GetMeResponse, type RedeemInviteRequest, type RedeemInviteResponse,
} from '@avs/contract';
import type { ApiClient } from './types.js';
import { getUserId } from '../state/userId.js';

/** 真实 fetch 实现。
 *  Slice 7 起：URL 前缀走 VITE_API_BASE（生产填 https://avs-bff.xxx.workers.dev）；
 *  本地 dev 不填则走 vite proxy（/api/* → http://localhost:8787）。
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

async function call<Res>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  data?: unknown,
): Promise<Res> {
  let url = API_BASE + path;
  const init: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-avs-user': getUserId(),   // Slice 9：匿名用户标识，BFF 用它做额度账户
    },
  };
  if (method === 'GET' && data) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (v != null && v !== '') qs.set(k, String(v));
    }
    const q = qs.toString();
    if (q) url += '?' + q;
  } else if (data != null) {
    init.body = JSON.stringify(data);
  }
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    const e = body?.error;
    throw new ApiError(e?.code ?? 'INTERNAL', e?.message ?? `HTTP ${res.status}`, e?.issues);
  }
  return (await res.json()) as Res;
}

export const realClient: ApiClient = {
  listBatches: (req: ListBatchesRequest) =>
    call<ListBatchesResponse>(routes.listBatches.path, 'GET', req),
  batchOverview: () =>
    call<BatchOverviewResponse>(routes.batchOverview.path, 'GET'),
  getBatch: (req: GetBatchRequest) =>
    call<GetBatchResponse>(routes.getBatch.path, 'GET', req),
  createBatch: (req: CreateBatchRequest) =>
    call<CreateBatchResponse>(routes.createBatch.path, 'POST', req),
  retryBatch: (req: RetryBatchRequest) =>
    call<RetryBatchResponse>(routes.retryBatch.path, 'POST', req),
  cancelBatch: (req: CancelBatchRequest) =>
    call<CancelBatchResponse>(routes.cancelBatch.path, 'POST', req),
  listAssets: (req: ListAssetsRequest) =>
    call<ListAssetsResponse>(routes.listAssets.path, 'GET', req),
  requestUpload: (req: RequestUploadUrlRequest) =>
    call<RequestUploadUrlResponse>(routes.requestUpload.path, 'POST', req),
  confirmUpload: (req: ConfirmUploadRequest) =>
    call<ConfirmUploadResponse>(routes.confirmUpload.path, 'POST', req),
  getConfig: () => call<AppConfig>(routes.getConfig.path, 'GET'),
  putConfig: (cfg: AppConfig) => call<Ok>(routes.putConfig.path, 'POST', cfg),
  getMe: () => call<GetMeResponse>(routes.getMe.path, 'GET'),
  redeemInvite: (req: RedeemInviteRequest) =>
    call<RedeemInviteResponse>(routes.redeemInvite.path, 'POST', req),
};

// 让 getUserId 在没有任何 GET-without-data 调用时也不被 tree-shake 警告
void getUserId;
